import React from 'react';
import { MapPin, TrendingUp, Globe, Building2, Award } from 'lucide-react';

const GlobalReachMap = () => {
  const regions = [
    {
      id: 'guinea',
      name: 'Guinea',
      role: 'Source',
      description: 'Direct partnerships with established mining operations in Guinea, providing consistent access to premium-grade bauxite reserves. Strategic location in West Africa enables efficient shipping routes to global markets.',
      position: { x: 478, y: 345 },
      labelPosition: { x: 380, y: 345 },
      color: '#D4621F',
      icon: TrendingUp,
      stats: 'Conakry'
    },
    {
      id: 'asia',
      name: 'Asia',
      role: 'Primary Market',
      description: 'Serving major aluminum producers across China, India, and Southeast Asia. Our extensive network ensures reliable supply chains to meet the growing demand from the world\'s largest aluminum production region.',
      position: { x: 780, y: 320 },
      labelPosition: { x: 850, y: 285 },
      color: '#00FFD1',
      icon: Globe,
      stats: 'China, India, SE Asia'
    },
    {
      id: 'middle-east',
      name: 'Middle East',
      role: 'Growing Market',
      description: 'Supporting the rapid expansion of aluminum smelting capacity in UAE and Saudi Arabia. Strategic partnerships with emerging facilities positioning for long-term growth in this dynamic region.',
      position: { x: 620, y: 330 },
      labelPosition: { x: 620, y: 400 },
      color: '#FFB366',
      icon: Building2,
      stats: 'UAE & Saudi Arabia'
    },
    {
      id: 'europe',
      name: 'Europe',
      role: 'Strategic Market',
      description: 'Delivering premium bauxite to established European aluminum producers with stringent quality requirements. Deep understanding of regulatory compliance and industry standards across European markets.',
      position: { x: 515, y: 275 },
      labelPosition: { x: 515, y: 205 },
      color: '#4A90E2',
      icon: Award,
      stats: 'UK, Netherlands, Germany'
    }
  ];

  return (
    <div className="global-reach-map-container">
      <svg 
        viewBox="0 0 1000 600" 
        className="world-map-svg"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="continentGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(255, 255, 255, 0.12)" />
            <stop offset="100%" stopColor="rgba(255, 255, 255, 0.06)" />
          </linearGradient>
          
          <filter id="mapShadow">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
            <feOffset dx="0" dy="2" result="offsetblur"/>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.3"/>
            </feComponentTransfer>
            <feMerge> 
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/> 
            </feMerge>
          </filter>

          <filter id="pinShadow">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.5"/>
          </filter>
        </defs>

        {/* Background */}
        <rect width="1000" height="600" fill="rgba(0,0,0,0.1)"/>
        
        {/* Subtle grid */}
        <g stroke="rgba(212, 98, 31, 0.08)" strokeWidth="0.5" fill="none">
          {[...Array(6)].map((_, i) => (
            <line key={`lat-${i}`} x1="0" y1={100 * (i + 1)} x2="1000" y2={100 * (i + 1)} />
          ))}
          {[...Array(10)].map((_, i) => (
            <line key={`lon-${i}`} x1={100 * (i + 1)} y1="0" x2={100 * (i + 1)} y2="600" />
          ))}
        </g>

        {/* World Continents */}
        <g className="world-continents" fill="url(#continentGradient)" stroke="rgba(212, 98, 31, 0.4)" strokeWidth="1.5" filter="url(#mapShadow)">
          {/* North America */}
          <path d="M 85,170 L 95,160 L 110,155 L 125,153 L 145,155 L 165,160 L 180,168 L 195,178 L 210,192 L 225,210 L 238,232 L 248,258 L 253,285 L 252,310 L 245,332 L 232,350 L 215,363 L 195,370 L 175,372 L 155,368 L 138,358 L 122,342 L 108,320 L 97,295 L 90,268 L 86,240 L 84,212 L 85,185 Z" />
          
          {/* South America */}
          <path d="M 268,365 L 283,360 L 298,358 L 313,361 L 326,370 L 337,384 L 346,402 L 352,425 L 355,450 L 354,475 L 348,497 L 336,515 L 318,528 L 296,535 L 272,533 L 250,523 L 232,507 L 220,486 L 214,462 L 215,437 L 222,413 L 234,392 L 250,376 Z" />
          
          {/* Europe */}
          <path d="M 482,220 L 495,216 L 508,215 L 520,216 L 532,220 L 543,227 L 552,237 L 558,250 L 560,265 L 558,278 L 550,289 L 538,296 L 524,299 L 510,298 L 498,292 L 488,282 L 482,268 L 480,252 L 481,235 Z" />
          
          {/* Africa */}
          <path d="M 468,290 L 483,288 L 498,289 L 513,293 L 527,301 L 539,314 L 548,332 L 554,353 L 556,376 L 554,400 L 548,425 L 538,448 L 524,468 L 506,484 L 486,494 L 465,498 L 445,496 L 426,488 L 410,475 L 398,458 L 390,438 L 386,415 L 386,392 L 391,368 L 401,346 L 416,327 L 434,311 L 452,298 Z" />
          
          {/* Asia */}
          <path d="M 568,200 L 590,195 L 615,193 L 642,196 L 672,204 L 704,218 L 737,238 L 768,263 L 795,292 L 815,323 L 828,355 L 833,387 L 830,416 L 820,440 L 803,459 L 780,472 L 753,480 L 724,483 L 695,478 L 667,466 L 641,447 L 618,423 L 598,394 L 583,360 L 573,323 L 568,285 L 567,247 L 569,218 Z" />
          
          {/* Australia */}
          <path d="M 763,425 L 788,421 L 813,420 L 838,423 L 860,431 L 878,444 L 890,461 L 896,481 L 896,501 L 890,519 L 878,534 L 860,544 L 838,549 L 813,549 L 788,544 L 766,534 L 750,519 L 741,499 L 738,477 L 741,457 L 750,441 Z" />
          
          {/* Middle East */}
          <path d="M 552,265 L 568,262 L 583,263 L 597,268 L 609,277 L 618,290 L 623,306 L 623,322 L 618,337 L 608,348 L 595,354 L 580,356 L 565,353 L 553,345 L 545,332 L 542,316 L 543,299 L 547,283 Z" opacity="0.8"/>
        </g>

        {/* Connection lines from Guinea */}
        {regions.map((region) => {
          if (region.id !== 'guinea') {
            return (
              <line
                key={`line-${region.id}`}
                x1="478"
                y1="345"
                x2={region.position.x}
                y2={region.position.y}
                stroke={region.color}
                strokeWidth="2"
                strokeDasharray="8,4"
                opacity="0.4"
                className="connection-line"
              />
            );
          }
          return null;
        })}

        {/* Region markers with pins and compact labels */}
        {regions.map((region) => (
          <g key={region.id} className="region-marker-group" filter="url(#pinShadow)">
            {/* Pin base */}
            <path
              d={`M ${region.position.x},${region.position.y} 
                  L ${region.position.x - 8},${region.position.y - 16}
                  Q ${region.position.x - 10},${region.position.y - 25} ${region.position.x},${region.position.y - 28}
                  Q ${region.position.x + 10},${region.position.y - 25} ${region.position.x + 8},${region.position.y - 16}
                  Z`}
              fill={region.color}
              stroke="#000"
              strokeWidth="1.5"
              className="map-pin"
            />
            
            {/* Pin head circle */}
            <circle
              cx={region.position.x}
              cy={region.position.y - 28}
              r="10"
              fill={region.color}
              stroke="#000"
              strokeWidth="2"
            />
            
            {/* Inner highlight */}
            <circle
              cx={region.position.x - 2.5}
              cy={region.position.y - 30.5}
              r="3.5"
              fill="rgba(255, 255, 255, 0.7)"
            />

            {/* Compact Label */}
            <g>
              {/* Label background */}
              <rect
                x={region.labelPosition.x - 55}
                y={region.labelPosition.y - 12}
                width="110"
                height="40"
                fill="rgba(18, 18, 18, 0.95)"
                stroke={region.color}
                strokeWidth="1.5"
                rx="3"
              />
              
              {/* Region name */}
              <text
                x={region.labelPosition.x}
                y={region.labelPosition.y + 5}
                textAnchor="middle"
                fill="#FFFFFF"
                fontSize="13"
                fontWeight="600"
              >
                {region.name}
              </text>
              
              {/* Role */}
              <text
                x={region.labelPosition.x}
                y={region.labelPosition.y + 20}
                textAnchor="middle"
                fill={region.color}
                fontSize="9"
                fontWeight="700"
                letterSpacing="0.5"
              >
                {region.role.toUpperCase()}
              </text>
            </g>
          </g>
        ))}
      </svg>

      {/* Detailed Stats Cards */}
      <div className="map-stats-section">
        <div className="map-stats-grid">
          {regions.map((region) => {
            const IconComponent = region.icon;
            return (
              <div key={region.id} className="map-stat-card">
                <div className="stat-card-header">
                  <div className="stat-icon" style={{ backgroundColor: region.color }}>
                    <IconComponent size={22} color="#000" />
                  </div>
                  <div className="stat-info">
                    <h4 className="stat-name">{region.name}</h4>
                    <span className="stat-role">{region.role}</span>
                  </div>
                </div>
                <p className="stat-description">{region.description}</p>
                <div className="stat-meta">
                  <MapPin size={14} color={region.color} />
                  <span>{region.stats}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GlobalReachMap;
